'use client';

import { logger } from '@dealerportal/core';
import { useClientContext } from '@dealerportal/react-helpers';
import { useCallback, useEffect, useState } from 'react';

interface UseShopifyScriptOptions {
  universalRetailerId?: string;
  shopifyDomain?: string;
  storefrontAccessToken?: string;
  buttonSelector?: string;
  autoRegister?: boolean;
  scriptUrl?: string;
  cssUrl?: string;
  onScriptLoad?: () => void;
  onScriptError?: (error: Error) => void;
}

interface UseShopifyScriptReturn {
  isLoaded: boolean;
  isLoading: boolean;
  loadScript: () => void;
  reregisterButtons: () => void;
  registerButton: (button: HTMLButtonElement) => void;
}

/**
 * Custom hook for managing Shopify script loading and button registration
 * Provides programmatic control over the Shopify integration
 */
export const useShopifyScript = (
  options: UseShopifyScriptOptions = {}
): UseShopifyScriptReturn => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const ctx = useClientContext();

  const {
    buttonSelector = 'button[id="adhawk-shopify-camilyo-marketplace-order-sample"]',
    autoRegister = true,
    universalRetailerId = ctx.universalRetailerId,
    shopifyDomain = process.env.NEXT_PUBLIC_SHOPIFY_DOMAIN,
    storefrontAccessToken = process.env
      .NEXT_PUBLIC_SHOPIFY_STOREFRONT_ACCESS_TOKEN,
    scriptUrl = 'https://adhawk-marketplace-assets.s3-us-west-1.amazonaws.com/v4/index-factory.js',
    onScriptLoad = () => {},
    onScriptError = () => {},
  } = options;

  const registerButton = useCallback((button: HTMLButtonElement) => {
    if (!(window as any)['EzCommerce']?.shopify) {
      logger.warn('EzCommerce not available, cannot register button');
      return;
    }

    if (
      button.classList.contains('shopify-buy-frame') ||
      button.getAttribute('data-shopify-registered') === 'true'
    ) {
      return;
    }

    try {
      (window as any)['EzCommerce'].shopify.createUIComponents(
        button,
        true,
        button.dataset
      );
      (window as any)['EzCommerce'].populateNotesFieldData(button.dataset);
      button.setAttribute('data-shopify-registered', 'true');

      // Re-attach cart iframe if it was removed from DOM during client-side navigation.
      // EzCommerceGenerator creates the cart node on fresh load; on soft nav it persists
      // in JS memory but its DOM node gets detached. Re-appending it restores the cart sheet.
      setTimeout(() => {
        const shopifyUi = (window as any)['EzCommerce']?.shopify?.ui;
        const cart = shopifyUi?.components?.cart?.[0];
        const node = cart?.node;
        if (node instanceof Node && !node.isConnected) {
          document.body.appendChild(node);
        }
      }, 200);
    } catch (error) {
      logger.withError(error).error('Error registering Shopify button:');
    }
  }, []);

  const reregisterButtons = useCallback(() => {
    if (!(window as any)['EzCommerce']?.shopify) return;

    const buttons: NodeListOf<HTMLButtonElement> =
      document.querySelectorAll(buttonSelector);

    for (const button of buttons) {
      registerButton(button);
    }
  }, [buttonSelector, registerButton]);

  const loadScript = useCallback(() => {
    // If already loading or loaded, don't proceed
    if (isLoading || isLoaded) return;

    // Check if script is already loaded
    const existingScript = document.querySelector(`script[src="${scriptUrl}"]`);
    if (existingScript) {
      // Script exists, check if EzCommerce is available
      if ((window as any)['EzCommerce']) {
        setIsLoaded(true);
      }
      return;
    }

    setIsLoading(true);

    // Load JavaScript
    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.src = scriptUrl;
    script.id = 'adhawk-shopify-camilyo-marketplace';

    script.onload = () => {
      setIsLoading(false);
      try {
        if (typeof (window as any)['EzCommerceGenerator'] === 'function') {
          const generatorConfig: Record<string, string> = {
            universalRetailerId: universalRetailerId || '',
          };
          // Only override the script's built-in defaults when both values are
          // provided — Shopify needs the domain and token together, and passing
          // empty strings would clobber the defaults.
          if (shopifyDomain && storefrontAccessToken) {
            generatorConfig.shopifyDomain = shopifyDomain;
            generatorConfig.storefrontAccessToken = storefrontAccessToken;
          }
          (window as any)['EzCommerceGenerator'](generatorConfig)();
          setIsLoaded(true);
          onScriptLoad();
        }
      } catch (error) {
        logger.withError(error).error('Error initializing EzCommerce:');
        onScriptError(error as Error);
      }
    };

    script.onerror = () => {
      setIsLoading(false);
      const error = new Error(`Failed to load script: ${scriptUrl}`);
      logger.withError(error).error('Failed to load Shopify script:');
      onScriptError(error);
    };

    document.head.appendChild(script);
  }, [
    isLoading,
    isLoaded,
    scriptUrl,
    universalRetailerId,
    shopifyDomain,
    storefrontAccessToken,
    onScriptLoad,
    onScriptError,
  ]);

  // Auto-register buttons when script becomes available
  useEffect(() => {
    if (isLoaded && autoRegister) {
      reregisterButtons();
    }
  }, [isLoaded, autoRegister, reregisterButtons]);

  return {
    isLoaded,
    isLoading,
    loadScript,
    reregisterButtons,
    registerButton,
  };
};
